/**
 * Parser for the /tournament query string.
 *
 * Shape:
 *   ?player1=<name>&player2=<name>&player3=<name>&player4=<name>&json=<URL>
 *
 * The `json` value is itself a fully-qualified URL with its own query string
 * (Azure Blob SAS token). Standard URLSearchParams would split that into
 * separate params, so we split the raw search string at the literal `json=`
 * boundary and take everything after it verbatim. By contract, `json` is
 * always the last parameter.
 */

export interface TournamentParams {
  players: [string, string, string, string];
  jsonUrl: string;
}

const PLAYER_KEYS = ['player1', 'player2', 'player3', 'player4'] as const;

function stripWrappingQuotes(value: string): string {
  return value.replace(/^["']|["']$/g, '').trim();
}

export function parseTournamentQuery(search: string): TournamentParams | null {
  if (!search) return null;

  const q = search.startsWith('?') ? search.slice(1) : search;

  // By contract, `json` is always the LAST parameter, so match the last
  // occurrence of `&json=` (or `json=` at position 0). Using `lastIndexOf`
  // is safer than `indexOf` against any earlier value that might happen
  // to contain the substring `json=`.
  const ampMarker = '&json=';
  let urlStart: number;
  let playerPartEnd: number;
  const lastAmp = q.lastIndexOf(ampMarker);
  if (lastAmp !== -1) {
    urlStart = lastAmp + ampMarker.length;
    playerPartEnd = lastAmp;
  } else if (q.startsWith('json=')) {
    urlStart = 'json='.length;
    playerPartEnd = 0;
  } else {
    return null;
  }

  // Everything after `json=` is the URL — left raw so the SAS token's
  // own URL-encoded characters (e.g. %3D) survive intact for fetch().
  const jsonUrl = q.slice(urlStart);
  if (!jsonUrl) return null;

  const playerPart = q.slice(0, playerPartEnd);
  const params = new URLSearchParams(playerPart);

  const players = PLAYER_KEYS.map((key) =>
    stripWrappingQuotes(params.get(key) ?? ''),
  );

  if (players.some((p) => !p)) return null;

  return {
    players: players as [string, string, string, string],
    jsonUrl,
  };
}
